import { useMemo, useState } from 'react'
import { Search, Users } from 'lucide-react'
import type { BlocoColleagueOption } from '@/hooks/useBlocoColleagues'
import { formatColleagueRole } from '@/hooks/useBlocoColleagues'

interface BlocoColleagueSelectProps {
  colleagues: BlocoColleagueOption[]
  selectedIds: string[]
  currentUserId?: string
  loading?: boolean
  onChange: (ids: string[]) => void
}

export function BlocoColleagueSelect({
  colleagues,
  selectedIds,
  currentUserId,
  loading = false,
  onChange,
}: BlocoColleagueSelectProps) {
  const [query, setQuery] = useState('')

  const options = useMemo(() => {
    const filtered = colleagues.filter((colleague) => colleague.id !== currentUserId)
    const normalized = query.trim().toLowerCase()
    if (!normalized) return filtered
    return filtered.filter((colleague) => {
      const roleLabel = formatColleagueRole(colleague.role).toLowerCase()
      return (
        colleague.name.toLowerCase().includes(normalized) ||
        roleLabel.includes(normalized) ||
        (colleague.role ?? '').toLowerCase().includes(normalized)
      )
    })
  }, [colleagues, currentUserId, query])

  const toggleColleague = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((item) => item !== id))
      return
    }
    onChange([...selectedIds, id])
  }

  return (
    <div className='mt-3 rounded-lg border border-[var(--glass-border)] bg-bg-card/60 p-3'>
      <div className='mb-2 flex items-center gap-1.5 text-xs font-medium text-text-primary'>
        <Users size={14} />
        Vincular usuários do CRM
      </div>

      {loading ? (
        <p className='text-xs text-text-muted'>Carregando usuários…</p>
      ) : colleagues.length === 0 ? (
        <p className='text-xs text-text-muted'>Nenhum usuário disponível para vincular.</p>
      ) : (
        <>
          <div className='relative mb-2'>
            <Search size={14} className='pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted' />
            <input
              type='search'
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder='Buscar por nome ou perfil…'
              className='w-full rounded-md border border-[var(--glass-border)] bg-bg-card py-1.5 pl-8 pr-2 text-xs text-text-primary outline-none placeholder:text-text-muted focus:border-[var(--brand-primary)]'
            />
          </div>

          {options.length === 0 ? (
            <p className='text-xs text-text-muted'>Nenhum usuário encontrado para &quot;{query}&quot;.</p>
          ) : (
            <div className='max-h-40 space-y-1 overflow-y-auto pr-1'>
              {options.map((colleague) => (
                <label
                  key={colleague.id}
                  className='flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm text-text-primary hover:bg-[var(--glass-bg)]'
                >
                  <input
                    type='checkbox'
                    checked={selectedIds.includes(colleague.id)}
                    onChange={() => toggleColleague(colleague.id)}
                    className='rounded border-[var(--glass-border)]'
                  />
                  <span className='min-w-0 flex-1 truncate'>{colleague.name}</span>
                  {colleague.role && (
                    <span className='shrink-0 rounded bg-[var(--glass-bg)] px-1.5 py-0.5 text-[10px] font-medium text-text-muted'>
                      {formatColleagueRole(colleague.role)}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
